<template>
  <div class="permission-tree-select">
    <!-- 权限模式切换 -->
    <div v-if="mode !== 'normal'" class="mode-switch">
      <var-space :size="6" wrap>
        <span style="font-size:12px;color:var(--var-text-secondary);">权限模式：</span>
        <var-button :type="currentMode === 'default' ? 'primary' : 'default'" size="small"
          @click="switchMode('default')">
          📌 默认权限
        </var-button>
        <var-button :type="currentMode === 'custom' ? 'warning' : 'default'" size="small" @click="switchMode('custom')">
          ✏️ 自定义权限
        </var-button>
        <span v-if="currentMode === 'default'" style="font-size:11px;color:#909399;">
          （权限随职务变化，只读）
        </span>
        <span v-else style="font-size:11px;color:#e6a23c;">
          （可手动修改，不随职务变化）
        </span>
      </var-space>
    </div>

    <!-- 权限树区域 -->
    <div :class="['permission-tree-wrapper', { 'is-disabled': isReadonly }]">
      <!-- 全选 -->
      <div class="header">
        <var-checkbox :model-value="isAllChecked" @update:model-value="handleCheckAll" :indeterminate="isIndeterminate">
          全选 ({{ checkedCount }}/{{ totalCount }})
        </var-checkbox>
      </div>

      <!-- ============================================================ -->
      <!-- 折叠面板 - 手风琴模式                                        -->
      <!-- ============================================================ -->
      <var-collapse v-model="expanded" accordion :offset="false" :divider="false">
        <var-collapse-item v-for="module in permissionData" :key="module.value" :name="module.value">
          <template #title>
            <div class="module-title-wrapper">
              <!-- ✅ 复选框替代图标 -->
              <var-checkbox v-if="!isReadonly" size="small" :model-value="isModuleAllChecked(module)"
                :indeterminate="isModuleIndeterminate(module)"
                @update:model-value="handleModuleCheckAll(module, $event)" @click.stop />
              <span class="module-name">{{ module.label }}</span>
              <div class="progress-bar">
                <div class="progress-track">
                  <div class="progress-fill" :style="{ width: getModuleProgress(module) + '%' }"
                    :class="getModuleProgressClass(module)"></div>
                </div>
                <span class="progress-text">{{ getModuleCheckedCount(module) }}/{{ getModuleTotalCount(module) }}</span>
              </div>
            </div>
          </template>

          <!-- 面板内容：权限列表 -->
          <div class="panel-content">
            <div class="feature-list">
              <var-chip v-for="child in module.children" :key="child.value"
                :type="selectedValues.includes(child.value) ? 'primary' : 'default'"
                :plain="!selectedValues.includes(child.value)" size="small" class="feature-chip"
                :class="{ 'chip-selected': selectedValues.includes(child.value) }"
                @click="togglePermission(child.value)">
                {{ child.label }}
              </var-chip>
            </div>
          </div>
        </var-collapse-item>
      </var-collapse>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { Snackbar } from '@varlet/ui'
import { PERMISSION_TREE } from '@/config/deviceFeatures'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  mode: {
    type: String,
    default: 'normal'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  positionPermissions: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'update:mode', 'change'])

const permissionData = ref(PERMISSION_TREE)

// ============================================================
// 初始化
// ============================================================
const selectedValues = ref([...props.modelValue])
const currentMode = ref(props.mode)
const defaultPermissions = ref([...props.positionPermissions])

// 手风琴模式：当前展开的模块名（默认不展开）
const expanded = ref('')

// ============================================================
// 模块级别
// ============================================================
const getModuleTotalCount = (module) => {
  return module.children.length
}

const getModuleCheckedCount = (module) => {
  let count = 0
  for (const child of module.children) {
    if (selectedValues.value.includes(child.value)) count++
  }
  return count
}

const getModuleProgress = (module) => {
  const total = getModuleTotalCount(module)
  if (total === 0) return 0
  return Math.round((getModuleCheckedCount(module) / total) * 100)
}

const getModuleProgressClass = (module) => {
  const progress = getModuleProgress(module)
  if (progress === 100) return 'progress-full'
  if (progress > 0) return 'progress-partial'
  return 'progress-empty'
}

// ============================================================
// ✅ 模块全选/半选状态
// ============================================================
const isModuleAllChecked = (module) => {
  const total = getModuleTotalCount(module)
  if (total === 0) return false
  return getModuleCheckedCount(module) === total
}

const isModuleIndeterminate = (module) => {
  const checked = getModuleCheckedCount(module)
  const total = getModuleTotalCount(module)
  return checked > 0 && checked < total
}

const handleModuleCheckAll = (module, checked) => {
  if (isReadonly.value) return

  const childValues = module.children.map(child => child.value)
  if (checked) {
    for (const value of childValues) {
      if (!selectedValues.value.includes(value)) {
        selectedValues.value.push(value)
      }
    }
  } else {
    for (const value of childValues) {
      const index = selectedValues.value.indexOf(value)
      if (index > -1) {
        selectedValues.value.splice(index, 1)
      }
    }
  }
  emitChange()
}

// ============================================================
// Watch
// ============================================================
watch(() => props.modelValue, (val) => {
  selectedValues.value = [...val]
}, { immediate: true, deep: true })

watch(() => props.mode, (val) => {
  currentMode.value = val
})

watch(() => props.positionPermissions, (val) => {
  defaultPermissions.value = [...val]
  if (currentMode.value === 'default') {
    selectedValues.value = [...val]
    emit('update:modelValue', [...val])
    emit('change', [...val])
  }
}, { deep: true })

// ============================================================
// Computed
// ============================================================
const totalCount = computed(() => {
  let count = 0
  for (const module of permissionData.value) {
    count += getModuleTotalCount(module)
  }
  return count
})

const checkedCount = computed(() => selectedValues.value.length)

const isAllChecked = computed(() => {
  return checkedCount.value === totalCount.value && totalCount.value > 0
})

const isIndeterminate = computed(() => {
  return checkedCount.value > 0 && checkedCount.value < totalCount.value
})

const isReadonly = computed(() => {
  if (currentMode.value === 'normal') {
    return props.disabled
  }
  return props.disabled || currentMode.value === 'default'
})

// ============================================================
// 权限操作
// ============================================================
const togglePermission = (value) => {
  if (isReadonly.value) return
  const index = selectedValues.value.indexOf(value)
  if (index > -1) {
    selectedValues.value.splice(index, 1)
  } else {
    selectedValues.value.push(value)
  }
  emitChange()
}

const handleCheckAll = (checked) => {
  if (isReadonly.value) return
  if (checked) {
    const allValues = []
    for (const module of permissionData.value) {
      for (const child of module.children) {
        if (!allValues.includes(child.value)) allValues.push(child.value)
      }
    }
    selectedValues.value = allValues
  } else {
    selectedValues.value = []
  }
  emitChange()
}

const emitChange = () => {
  const sortedValues = [...new Set(selectedValues.value)].sort()
  emit('update:modelValue', sortedValues)
  emit('change', sortedValues)
}

// ============================================================
// 模式切换
// ============================================================
const switchMode = (mode) => {
  if (mode === currentMode.value) return
  currentMode.value = mode
  emit('update:mode', mode)
  if (mode === 'default') {
    const perms = [...defaultPermissions.value]
    selectedValues.value = perms
    emit('update:modelValue', perms)
    emit('change', perms)
    Snackbar.success('已切换到默认模式')
  } else if (mode === 'custom') {
    Snackbar.success('已切换到自定义模式')
  }
}

// ============================================================
// 对外暴露
// ============================================================
const updateDefaultPermissions = (permissions) => {
  defaultPermissions.value = [...permissions]
  if (currentMode.value === 'default') {
    selectedValues.value = [...permissions]
    emit('update:modelValue', [...permissions])
    emit('change', [...permissions])
  }
}

const setSelectedValues = (values) => {
  selectedValues.value = [...values]
  emit('update:modelValue', [...values])
  emit('change', [...values])
}

defineExpose({
  setSelectedValues,
  updateDefaultPermissions,
  switchMode,
  getCurrentMode: () => currentMode.value,
  getSelectedValues: () => [...selectedValues.value]
})
</script>

<style scoped>
.permission-tree-select {
  padding: 4px 0;
}

.mode-switch {
  padding-bottom: 6px;
  margin-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.permission-tree-wrapper.is-disabled {
  opacity: 0.6;
  pointer-events: none;
  cursor: not-allowed;
}

/* ============================================================ */
/* Header */
/* ============================================================ */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 6px;
  margin-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.header :deep(.var-checkbox__label) {
  font-size: 13px;
}

/* ============================================================ */
/* 折叠面板 - 模块行 */
/* ============================================================ */
.module-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  width: 100%;
}

.module-title-wrapper :deep(.var-checkbox) {
  --checkbox-size: 16px;
  flex-shrink: 0;
}

.module-title-wrapper :deep(.var-checkbox__label) {
  font-size: 12px;
  color: #666;
}

.module-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  flex-shrink: 0;
  min-width: 60px;
}

/* 进度条 */
.progress-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 60px;
}

.progress-track {
  flex: 1;
  height: 6px;
  background: #e8ecf1;
  border-radius: 3px;
  overflow: hidden;
  min-width: 40px;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.4s ease;
}

.progress-fill.progress-full {
  background: #52c41a;
}

.progress-fill.progress-partial {
  background: #2979ff;
}

.progress-fill.progress-empty {
  background: #d0d5dd;
}

.progress-text {
  font-size: 12px;
  color: #888;
  font-weight: 500;
  min-width: 32px;
  text-align: right;
  flex-shrink: 0;
}

/* ============================================================ */
/* 面板内容 */
/* ============================================================ */
.panel-content {
  padding: 4px 0 6px 0;
}

.feature-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 0 2px 0;
}

.feature-chip {
  font-size: 12px;
  padding: 0 10px;
  height: 26px;
  line-height: 26px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
}

.feature-chip.chip-selected {
  background: #2979ff;
  color: #fff;
  border-color: #2979ff;
}

.feature-chip:not(.chip-selected):hover {
  background: #ecf5ff;
  border-color: #2979ff;
}

/* ============================================================ */
/* 修复 Varlet Collapse 样式 */
/* ============================================================ */
:deep(.var-collapse-item__header) {
  padding: 8px 12px !important;
  min-height: 44px;
}

:deep(.var-collapse-item__content) {
  padding: 4px 12px 10px 12px !important;
}

:deep(.var-collapse-item__header-icon) {
  font-size: 16px !important;
}

:deep(.var-collapse-item) {
  border-bottom: 1px solid #f0f2f5 !important;
}

:deep(.var-collapse-item:last-child) {
  border-bottom: none !important;
}

/* ============================================================ */
/* 响应式 */
/* ============================================================ */
@media (max-width: 768px) {
  .module-name {
    font-size: 13px;
    min-width: 50px;
  }

  .progress-bar {
    min-width: 50px;
    flex: 0 1 100px;
  }

  .progress-text {
    font-size: 11px;
    min-width: 28px;
  }

  .feature-chip {
    font-size: 11px;
    padding: 0 8px;
    height: 24px;
    line-height: 24px;
  }

  .module-title-wrapper :deep(.var-checkbox) {
    --checkbox-size: 14px;
  }

  :deep(.var-collapse-item__header) {
    padding: 6px 10px !important;
    min-height: 38px;
  }

  :deep(.var-collapse-item__content) {
    padding: 2px 8px 6px 8px !important;
  }
}
</style>